// In phiếu thu tiền thẻ (K80 / A4) và sao kê thẻ liệu trình cho khách — in qua iframe ẩn, lưu PDF bằng hộp thoại in
import dayjs from 'dayjs';
import { BRANCHES, METHOD_LABEL, staffName } from '../POS/data';
import { currentUserName, formatPhone } from '../POS/utils';
import { KIND_LABEL, type TreatmentCard, type TreatmentConfig } from './data';
import { cardBalance, cardFlags, expiryText, installmentRows, lineBalances, outstanding, paidAmount, paymentAllocation } from './logic';
import useTreatmentStore from './store';

const money = (n: number) => new Intl.NumberFormat('vi-VN').format(Math.round(n || 0));
const d = (s: string) => dayjs(s).format('DD/MM/YYYY');
const dt = (s: string) => dayjs(s).format('DD/MM/YYYY HH:mm');
const esc = (s: string) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
const row = (label: string, value: string, cls = '') => `<tr class="${cls}"><td>${label}</td><td class="r">${value}</td></tr>`;

const CSS = {
  K80: '@page{size:80mm auto;margin:3mm} body{width:74mm;font-size:12px}',
  A4: '@page{size:A4;margin:16mm} body{font-size:13px;max-width:720px;margin:0 auto}',
};
const BASE_CSS = `body{font-family:Arial,Helvetica,sans-serif;color:#111}
  h1{font-size:16px;margin:0;text-align:center} h2{font-size:14px;margin:10px 0 4px} .c{text-align:center} .muted{color:#555;font-size:11px}
  table{width:100%;border-collapse:collapse} td,th{padding:2px 0;vertical-align:top;text-align:left} .r{text-align:right;white-space:nowrap}
  hr{border:0;border-top:1px dashed #999;margin:6px 0} .strong td{font-weight:700;font-size:1.15em}
  .reprint{border:1px solid #111;text-align:center;font-weight:700;margin:4px 0;padding:2px}
  .grid td,.grid th{border:1px solid #bbb;padding:3px 5px} .grid th{background:#eef5f7}
  .sign{display:flex;justify-content:space-between;margin-top:28px;text-align:center} .sign div{width:45%} .sign i{display:block;color:#555;font-size:11px;margin-bottom:48px}`;

export function printHtml(title: string, size: 'K80' | 'A4', body: string) {
  const html = wrapHtml(title, size, body);
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
  document.body.appendChild(frame);
  const doc = frame.contentWindow!.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    frame.contentWindow!.focus();
    frame.contentWindow!.print();
    setTimeout(() => frame.remove(), 1000);
  }, 200);
}

const header = (branchId: string, title: string) => {
  const b = BRANCHES.find(x => x.id === branchId);
  return `<h1>KHOTHI</h1><div class="c muted">${esc(b?.name || '')}</div><div class="c" style="font-weight:700;margin-top:6px">${title}</div>`;
};

/** Mã phiếu hiển thị: mã giao dịch POS nếu thu qua POS, không thì mã thanh toán của thẻ */
export const receiptNo = (p: { id: string; posTxnId?: string }) => p.posTxnId || p.id;

/** Nội dung phiếu thu cho một lần đóng tiền thẻ: đợt được thanh toán, đã đóng, còn nợ, đợt kế tiếp */
export function cardReceiptBody(card: TreatmentCard, paymentId: string, size: 'K80' | 'A4', opts: { change?: number; tendered?: number } = {}) {
  const a = paymentAllocation(card, paymentId);
  if (!a) return null;
  const p = a.payment;
  const no = receiptNo(p);
  const reprint = card.events.some(e => e.kind === 'print' && e.text.includes(no));
  const body = `
    ${header(p.branchId || card.branchId, 'PHIẾU THU TIỀN THẺ LIỆU TRÌNH')}
    ${reprint ? '<div class="reprint">BẢN IN LẠI</div>' : ''}
    <table>
      ${row('Số phiếu', esc(no))}
      ${row('Thời gian', dt(p.at))}
      ${row('Thu ngân', esc(p.by))}
      ${row('Khách hàng', esc(card.customerName) + (card.customerPhone ? ` · ${formatPhone(card.customerPhone)}` : ''))}
      ${row('Thẻ', esc(card.id))}
      ${row('Gói', esc(card.packageName))}
    </table><hr/>
    <div style="font-weight:700">Thanh toán cho</div>
    <table>
      ${a.items.map(i => row(`Đợt ${i.no}/${card.installments.length} (hạn ${d(i.dueDate)})${i.leftAfter ? ` – còn thiếu ${money(i.leftAfter)}` : ' – đủ'}`, money(i.applied))).join('')}
      ${a.excess > 0 ? row('Trả trước', money(a.excess)) : ''}
      ${row('SỐ TIỀN THU', `${money(p.amount)}đ`, 'strong')}
      ${row('Phương thức', METHOD_LABEL[p.method] + (p.txnRef ? ` (${esc(p.txnRef)})` : ''))}
      ${opts.tendered ? row('Khách đưa', money(opts.tendered)) : ''}
      ${opts.change ? row('Tiền thối', money(opts.change)) : ''}
    </table><hr/>
    <table>
      ${row('Giá thẻ', money(card.price))}
      ${row('Đã thanh toán', money(a.paidAfter))}
      ${row('Còn nợ', `${money(a.debtAfter)}đ`, a.debtAfter ? 'strong' : '')}
      ${a.next ? row('Đợt kế tiếp', `${d(a.next.dueDate)} · ${money(a.next.left)}`) : row('Tình trạng', 'Đã thanh toán đủ')}
    </table>
    ${size === 'A4' ? '<div class="sign"><div><b>Khách hàng</b><i>(Ký, ghi rõ họ tên)</i></div><div><b>Thu ngân</b><i>(Ký, ghi rõ họ tên)</i></div></div>' : ''}
    <hr/><div class="c muted">Vui lòng giữ phiếu để đối chiếu. Cảm ơn quý khách!</div>
    <div class="c muted">In lúc ${dt(new Date().toISOString())}</div>`;
  return { no, reprint, body };
}

export const wrapHtml = (title: string, size: 'K80' | 'A4', body: string) =>
  `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>${CSS[size]} ${BASE_CSS}</style></head><body>${body}</body></html>`;

/** In phiếu thu (lần đầu hoặc in lại — bản in lại có dấu BẢN IN LẠI) */
export function printCardReceipt(card: TreatmentCard, paymentId: string, size: 'K80' | 'A4', opts: { change?: number; tendered?: number } = {}) {
  const r = cardReceiptBody(card, paymentId, size, opts);
  if (!r) return;
  printHtml(r.no, size, r.body);
  useTreatmentStore.getState().logPrint(card.id, currentUserName(), `${r.reprint ? 'In lại' : 'In'} phiếu thu ${r.no} (${size})`);
}

/** Nội dung sao kê thẻ cho khách: số dư từng dịch vụ, lịch thanh toán, các lần đóng tiền, lịch sử sử dụng */
export function cardStatementBody(card: TreatmentCard, cfg: TreatmentConfig) {
  const b = cardBalance(card);
  const f = cardFlags(card, cfg);
  const rows = installmentRows(card, cfg);
  const usages = [...card.usages].filter(u => !u.cancelled).sort((x, y) => y.at.localeCompare(x.at));
  const stateLabel: Record<string, string> = { paid: 'Đã thu', overdue: 'Quá hạn', due_soon: 'Sắp đến hạn', upcoming: 'Chưa đến hạn', void: 'Không thu' };
  const body = `
    ${header(card.branchId, 'SAO KÊ THẺ LIỆU TRÌNH')}
    <div class="c muted">Ngày in ${d(new Date().toISOString())}</div>
    <h2>Thông tin thẻ</h2>
    <table>
      ${row('Khách hàng', esc(card.customerName) + (card.customerPhone ? ` · ${formatPhone(card.customerPhone)}` : ''))}
      ${row('Mã thẻ', esc(card.id))}
      ${row('Gói', `${esc(card.packageName)} (${KIND_LABEL[card.kind]})`)}
      ${row('Ngày mua · hạn dùng', `${d(card.createdAt)} · ${expiryText(card)}`)}
      ${row('Số dư', card.kind === 'session' ? `${b.remainingSessions}/${b.totalSessions} buổi` : `${money(b.remainingValue)}đ / ${money(b.faceValue)}đ`)}
      ${row('Giá thẻ · đã thanh toán · còn nợ', `${money(card.price)} · ${money(paidAmount(card))} · <b>${money(outstanding(card))}</b>`)}
      ${f.overdue ? row('Quá hạn', `<b>${money(f.overdueAmount)}đ</b>`) : ''}
    </table>
    ${card.kind === 'session' ? `<h2>Dịch vụ trong thẻ</h2>
    <table class="grid"><tr><th>Dịch vụ</th><th class="r">Theo gói</th><th class="r">Tặng/điều chỉnh</th><th class="r">Đã dùng</th><th class="r">Còn lại</th></tr>
      ${lineBalances(card).map(l => `<tr><td>${esc(l.name)}${l.role === 'bonus' ? ' (tặng)' : ''}</td><td class="r">${l.total}</td><td class="r">${l.bonus || ''}</td><td class="r">${l.used}</td><td class="r"><b>${Math.max(0, l.available)}</b></td></tr>`).join('')}
    </table>` : ''}
    <h2>Lịch thanh toán</h2>
    <table class="grid"><tr><th>Đợt</th><th>Đến hạn</th><th class="r">Phải thu</th><th class="r">Đã thu</th><th class="r">Còn thiếu</th><th>Trạng thái</th></tr>
      ${rows.map(r => `<tr><td>${r.no}</td><td>${d(r.dueDate)}</td><td class="r">${money(r.amount)}</td><td class="r">${money(r.paid)}</td><td class="r">${r.left ? money(r.left) : ''}</td><td>${stateLabel[r.state]}${r.state === 'overdue' ? ` ${r.overdueDays} ngày` : ''}</td></tr>`).join('')}
    </table>
    <h2>Các lần thanh toán</h2>
    <table class="grid"><tr><th>Số phiếu</th><th>Thời gian</th><th>Phương thức</th><th class="r">Số tiền</th></tr>
      ${card.payments.map(p => `<tr><td>${esc(receiptNo(p))}</td><td>${dt(p.at)}</td><td>${METHOD_LABEL[p.method]}</td><td class="r">${money(p.amount)}</td></tr>`).join('') || '<tr><td colspan="4">Chưa có</td></tr>'}
    </table>
    <h2>Lịch sử sử dụng</h2>
    <table class="grid"><tr><th>Thời gian</th><th>Dịch vụ</th><th>Chi nhánh · KTV</th></tr>
      ${usages.map(u => `<tr><td>${dt(u.at)}</td><td>${u.items.map(i => `${esc(i.name)} ×${i.qty}`).join(', ')}${card.kind === 'value' ? ` (-${money(u.items.reduce((s, i) => s + i.value, 0))}đ)` : ''}</td><td>${esc(BRANCHES.find(x => x.id === u.branchId)?.short || '')} · ${esc(staffName(u.staffId) || u.staffId)}</td></tr>`).join('') || '<tr><td colspan="3">Chưa sử dụng</td></tr>'}
    </table>
    <div class="sign"><div><b>Khách hàng</b><i>(Ký, ghi rõ họ tên)</i></div><div><b>Đại diện Khothi</b><i>(Ký, ghi rõ họ tên)</i></div></div>`;
  return body;
}

/** In sao kê thẻ A4 */
export function printCardStatement(card: TreatmentCard, cfg: TreatmentConfig) {
  printHtml(`Sao ke ${card.id}`, 'A4', cardStatementBody(card, cfg));
  useTreatmentStore.getState().logPrint(card.id, currentUserName(), 'In sao kê thẻ (A4)');
}
